import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { X, FileText, Upload } from "lucide-react";

const STATUSES = ["Open", "In Progress", "Completed"];
const EMPTY = {
  title: "", meeting_datetime: "", organization: "", discussion_notes: "",
  key_decisions: "", action_items: "", follow_up_date: "", status: "Open",
};

export default function MeetingDiscussionFormDialog({ open, onOpenChange, discussion, employees, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [internalIds, setInternalIds] = useState([]);
  const [externalText, setExternalText] = useState("");
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!discussion;

  useEffect(() => {
    if (open) {
      setForm(
        discussion
          ? {
              title: discussion.title, meeting_datetime: discussion.meeting_datetime || "",
              organization: discussion.organization || "", discussion_notes: discussion.discussion_notes || "",
              key_decisions: discussion.key_decisions || "", action_items: discussion.action_items || "",
              follow_up_date: discussion.follow_up_date || "", status: discussion.status || "Open",
            }
          : EMPTY
      );
      setInternalIds(discussion?.participants_internal || []);
      setExternalText((discussion?.participants_external || []).join(", "));
      setExistingAttachments(discussion?.attachments || []);
      setNewFiles([]);
    }
  }, [open, discussion]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleInternal = (id) => {
    setInternalIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const addFiles = (fileList) => setNewFiles((prev) => [...prev, ...Array.from(fileList)]);

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.meeting_datetime) {
      toast.error("Title and Date & Time are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("meeting_datetime", form.meeting_datetime);
      fd.append("organization", form.organization || "");
      fd.append("discussion_notes", form.discussion_notes || "");
      fd.append("key_decisions", form.key_decisions || "");
      fd.append("action_items", form.action_items || "");
      fd.append("follow_up_date", form.follow_up_date || "");
      fd.append("status", form.status);
      fd.append("participants_internal", JSON.stringify(internalIds));
      fd.append("participants_external", externalText || "");
      newFiles.forEach((f) => fd.append("files", f));
      if (isEdit) {
        fd.append("keep_attachments", JSON.stringify(existingAttachments.map((a) => a.file_path)));
        await api.put(`/meeting-discussions/${discussion.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Meeting discussion updated");
      } else {
        await api.post("/meeting-discussions", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Meeting discussion logged");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="meeting-discussion-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Meeting Discussion" : "Log Meeting Discussion"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Meeting Title</Label>
              <Input data-testid="meeting-discussion-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Vendor Onboarding - GreenPack Supplies" />
            </div>
            <div className="space-y-1.5">
              <Label>Date & Time</Label>
              <Input data-testid="meeting-discussion-datetime-input" type="datetime-local" value={form.meeting_datetime} onChange={set("meeting_datetime")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Organization</Label>
              <Input data-testid="meeting-discussion-organization-input" value={form.organization} onChange={set("organization")} placeholder="e.g. GreenPack Supplies Pvt. Ltd." />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="meeting-discussion-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Internal Participants</Label>
              <div className="border border-[#E2E8F0] rounded-md p-2.5 max-h-32 overflow-y-auto space-y-1.5" data-testid="meeting-discussion-internal-participants">
                {(employees || []).length === 0 && <p className="text-xs text-[#4B5563] px-1">No employees found.</p>}
                {(employees || []).map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2 text-sm px-1 py-0.5 cursor-pointer">
                    <Checkbox
                      data-testid={`meeting-discussion-participant-${emp.id}`}
                      checked={internalIds.includes(emp.id)}
                      onCheckedChange={() => toggleInternal(emp.id)}
                    />
                    {emp.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>External Participants</Label>
              <Textarea
                data-testid="meeting-discussion-external-participants-input"
                value={externalText}
                onChange={(e) => setExternalText(e.target.value)}
                rows={4}
                placeholder="Comma-separated names, e.g. Rakesh Sharma (GreenPack), Anita Rao (Vendor Ops)"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Discussion Notes</Label>
            <Textarea data-testid="meeting-discussion-notes-input" value={form.discussion_notes} onChange={set("discussion_notes")} rows={3} placeholder="What was discussed..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Key Decisions</Label>
              <Textarea data-testid="meeting-discussion-decisions-input" value={form.key_decisions} onChange={set("key_decisions")} rows={3} />
            </div>
            <div className="space-y-1.5">
              <Label>Action Items</Label>
              <Textarea data-testid="meeting-discussion-action-items-input" value={form.action_items} onChange={set("action_items")} rows={3} />
            </div>
          </div>
          <div className="space-y-1.5 w-48">
            <Label>Follow-up Date</Label>
            <Input data-testid="meeting-discussion-followup-date-input" type="date" value={form.follow_up_date} onChange={set("follow_up_date")} />
          </div>

          <div className="space-y-1.5">
            <Label>File Attachments</Label>
            <label className="flex items-center justify-center gap-2 border border-dashed border-[#E2E8F0] rounded-md py-3 cursor-pointer text-sm text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
              <Upload className="h-4 w-4" /> Add files
              <input type="file" multiple className="hidden" data-testid="meeting-discussion-files-input" onChange={(e) => e.target.files.length && addFiles(e.target.files)} />
            </label>
            {(existingAttachments.length > 0 || newFiles.length > 0) && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {existingAttachments.map((a) => (
                  <AttachmentThumb key={a.file_path} name={a.original_filename} src={a.content_type?.startsWith("image/") ? fileUrl(a.file_path) : null}
                    onRemove={() => setExistingAttachments((prev) => prev.filter((x) => x.file_path !== a.file_path))} testId={`remove-existing-attachment-${a.file_path}`} />
                ))}
                {newFiles.map((f, i) => (
                  <AttachmentThumb key={i} name={f.name} src={f.type.startsWith("image/") ? URL.createObjectURL(f) : null}
                    onRemove={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))} testId={`remove-new-attachment-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="meeting-discussion-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="meeting-discussion-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Log Meeting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentThumb({ name, src, onRemove, testId }) {
  return (
    <div className="relative group border border-[#E2E8F0] rounded-md h-16 flex items-center justify-center overflow-hidden bg-[#F8F9FA]">
      {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : <FileText className="h-5 w-5 text-[#4B5563]" />}
      <button type="button" data-testid={testId} onClick={onRemove} className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-black/60 text-white flex items-center justify-center">
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}
